import { useCallback, useRef, useState } from 'react';

import { logError } from '../lib/logger';
import { WIDGET_REMOVAL_ACTION, removeWidget, widgetRemovalAction } from '../lib/widgetRemoval';

export function useRemoveWidget({ source, onAfterRemove } = {}) {
  const [session, setSession] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const isRemovingRef = useRef(false);

  // `context` carries caller-specific data (e.g. boardItemId) for cleanup outside this hook.
  const openConfirm = useCallback((widget, context = null) => {
    if (isRemovingRef.current) return;
    setSession({ widget, context, action: widgetRemovalAction(widget) });
  }, []);
  const closeConfirm = useCallback(() => setSession(null), []);

  const confirmRemoval = useCallback(async () => {
    const current = session;
    const widget = current?.widget;
    if (!widget || isRemovingRef.current) return;

    isRemovingRef.current = true;
    setIsRemoving(true);
    setSession(null);

    try {
      try {
        await removeWidget(widget, current.action);
      } catch (error) {
        logError(source || 'useRemoveWidget', `Failed to ${current.action} widget`, { error, widgetId: widget.id });
        alert(
          current.action === WIDGET_REMOVAL_ACTION.UNPUBLISH
            ? 'Could not unpublish widget. Please try again.'
            : 'Could not delete widget. Please try again.',
        );
        return;
      }

      try {
        if (onAfterRemove) await onAfterRemove(current);
      } catch (error) {
        // The server-side removal succeeded. Log local follow-up failures without
        // inviting the user to retry an operation that has already completed.
        logError(source || 'useRemoveWidget', 'Failed to clean up removed widget', { error, widgetId: widget.id });
      }
    } finally {
      isRemovingRef.current = false;
      setIsRemoving(false);
    }
  }, [session, onAfterRemove, source]);

  return { session, openConfirm, closeConfirm, confirmRemoval, isRemoving };
}
